import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '../lib/api';

export default function KbPage() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const { data: cats = [] } = useQuery({ queryKey: ['kb-cats'], queryFn: async () => (await api.get('/kb/categories')).data });
  const { data: articles = [] } = useQuery({
    queryKey: ['kb-articles', { search, filterStatus }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterStatus) params.set('status', filterStatus);
      return (await api.get(`/kb/articles?${params}`)).data;
    },
  });

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-semibold">Knowledge Base</h1><p className="text-sm text-slate-500">{(articles as unknown[]).length} articles · {(cats as unknown[]).length} categories</p></div>
        <button className="btn-primary" onClick={() => setShowNew(true)}>+ New article</button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="text-xs text-slate-500 uppercase mb-2">Categories</div>
          <ul className="space-y-1 text-sm">
            {(cats as { id: string; name: string; _count: { articles: number } }[]).map((c) => (
              <li key={c.id} className="flex justify-between"><span>{c.name}</span><span className="text-slate-500">{c._count.articles}</span></li>
            ))}
          </ul>
        </div>
        <div className="col-span-3 space-y-3">
          <div className="card p-3 flex gap-2">
            <input className="input max-w-md" placeholder="Search articles…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <select className="input max-w-[170px]" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All statuses</option>
              {['DRAFT', 'PUBLISHED', 'ARCHIVED'].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr><th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Views</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(articles as { id: string; title: string; status: string; views: number; category?: { name: string } | null }[]).map((a) => (
                  <tr key={a.id}>
                    <td className="px-4 py-3 font-medium">{a.title}</td>
                    <td className="px-4 py-3 text-slate-600">{a.category?.name ?? '—'}</td>
                    <td className="px-4 py-3"><span className="badge bg-slate-100">{a.status}</span></td>
                    <td className="px-4 py-3 text-slate-600">{a.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showNew && <NewArticleModal cats={cats as { id: string; name: string }[]} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); qc.invalidateQueries({ queryKey: ['kb-articles'] }); }} />}
    </div>
  );
}

function NewArticleModal({ cats, onClose, onCreated }: { cats: { id: string; name: string }[]; onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [categoryId, setCategoryId] = useState(cats[0]?.id ?? '');
  const [status, setStatus] = useState<'DRAFT' | 'PUBLISHED'>('DRAFT');
  const create = useMutation({
    mutationFn: async () => api.post('/kb/articles', { title, body, categoryId: categoryId || undefined, status }),
    onSuccess: () => { toast.success('Article created'); onCreated(); },
    onError: (e: { response?: { data?: { error?: string } } }) => toast.error(e.response?.data?.error ?? 'Failed'),
  });
  return (
    <div className="fixed inset-0 bg-slate-900/40 grid place-items-center p-4 z-50">
      <div className="card w-full max-w-2xl p-6">
        <h2 className="text-lg font-semibold mb-4">New KB article</h2>
        <div className="space-y-3">
          <div><label className="label">Title</label><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Category</label>
              <select className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">— None</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><label className="label">Status</label>
              <select className="input" value={status} onChange={(e) => setStatus(e.target.value as 'DRAFT' | 'PUBLISHED')}>
                <option>DRAFT</option><option>PUBLISHED</option>
              </select>
            </div>
          </div>
          <div><label className="label">Body (markdown)</label><textarea className="input min-h-[200px]" value={body} onChange={(e) => setBody(e.target.value)} /></div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!title || !body || create.isPending} onClick={() => create.mutate()}>{create.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
